// src/common/invoiceSettings.schema.ts
import * as Yup from 'yup'
import type {
  InvoiceSettingsAutoInvoiceData,
  InvoiceSettingsData,
  InvoiceSettingsFormData,
} from '@/api/dto/settings.dto'

export const INITIAL_INVOICE_SETTINGS_FORM_VALUES: InvoiceSettingsFormData = {
  bankName: '',
  iban: '',
  bic: '',
  taxNumber: null,
  vatId: null,
  vatPercentage: 19,
  invoiceNumberPrefix: 'RE',
  offerNumberPrefix: '',

  issuerType: 'PERSON',
  accountHolder: '',

  // profile-backed UI fields
  firstName: '',
  lastName: '',
  company: '',
  street: '',
  streetNumber: '',
  postal: '',
  city: '',
  phone: '',
  email: '',
}

export const INITIAL_INVOICE_SETTINGS_AUTO_INVOICE_VALUES: InvoiceSettingsAutoInvoiceData = {
  autoGenerationEnabled: false,
  autoGenerationDay: null,
  autoGenerationTime: null,

  invoiceTemplate: '',
  activityInvoiceResetPolicy: null,
  activityInvoicePrefix: '',
  activityInvoiceNumberTemplate: '',
  activityInvoiceRunningStart: '0001',
  sendAfterAutoGeneration: true,
  invoicePreTextTemplate: '',
  invoicePostTextTemplate: '',
}

const IssuerTypeSchema = Yup.mixed<'COMPANY' | 'PERSON' | 'COMPANY_PERSON'>()
  .oneOf(['COMPANY', 'PERSON', 'COMPANY_PERSON'])
  .required('Bitte Rechnungssteller auswählen')

const requiredTrimmed = (msg: string) =>
  Yup.string()
    .transform(v => (typeof v === 'string' ? v.trim() : v))
    .required(msg)

/**
 * A VAT identification number: a country prefix followed by alphanumerics. Kept in step with
 * ValidationPatterns.VAT_COUNTRY_PREFIX on the backend — EU member states plus EL/GR, XI, and
 * the non-EU countries this platform issues invoices from.
 */
const VAT_ID_PATTERN =
  /^(AT|BE|BG|CH|CY|CZ|DE|DK|EE|EL|ES|FI|FR|GB|GR|HR|HU|IE|IT|LT|LU|LV|MT|NL|NO|PL|PT|RO|SE|SI|SK|XI)[A-Z0-9]+$/

/**
 * What a user types and what the invoice XML needs are not the same string. A VAT ID is
 * routinely pasted as it appears on a document — "DE 123 456 789", "de-123456789" — while
 * BT-31 must carry an uppercase country code followed by unbroken alphanumerics, or the
 * recipient rejects the invoice on BR-CO-09.
 *
 * Rejecting those inputs would be user-hostile; sending them as typed would put an invalid
 * identifier in the XML. So we normalise, validate the normalised form, and submit it —
 * the backend pattern does not accept separators, so the value has to be clean before it
 * leaves the form.
 */
export const normaliseVatId = (value: string) =>
  value.replace(/[\s.-]/g, '').toUpperCase()


// YUP SCHEMA
export const InvoiceSettingsSchema = Yup.object({
  bankName: Yup.string()
    .trim()
    .min(2, 'Mindestens 2 Zeichen')
    .required('Dieses Feld muss ausgefüllt sein'),

  iban: Yup.string()
    .matches(/^$|^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/, 'Ungültiges IBAN-Format')
    .required('Dieses Feld muss ausgefüllt sein'),

  bic: Yup.string()
    .max(11, 'Maximal 11 Zeichen')
    .matches(/^$|^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/, 'Ungültiger BIC')
    .required('Dieses Feld muss ausgefüllt sein'),

  taxNumber: Yup.string()
    .nullable()
    .transform(v => (v === '' ? null : v))
    .max(50, 'Maximal 50 Zeichen')
    .test(
      'tax-or-vat-required',
      'Bei Umsatzsteuer > 0 % wird eine Umsatzsteuer-ID oder Steuernummer benötigt',
      function (value) {
        // EN 16931 BR-S-02: standard-rated invoices need at least one seller tax identifier.
        const { vatId, vatPercentage } = this.parent
        if (Number(vatPercentage) > 0 && !value && !vatId) return false
        return true
      },
    )
    // Only a real country prefix means the value belongs in the VAT field — a tax number
    // abroad may legitimately contain letters, so "any two letters" would be too blunt.
    .test(
      'tax-number-not-vat-id',
      'Die Steuernummer darf nicht mit einem Länderkürzel beginnen. Eine Umsatzsteuer-ID (z. B. DE123456789) gehört in das Feld Umsatzsteuer-ID.',
      value => !value || !VAT_ID_PATTERN.test(normaliseVatId(value)),
    )
    // The swap that actually happens: a Steuernummer typed into Umsatzsteuer-ID while this
    // field is left empty. Reported here rather than on vatId so the format error and the
    // "wrong box" hint land on different fields instead of stacking under one.
    .test(
      'tax-number-sitting-in-vat-field',
      'Ihre Steuernummer steht offenbar im Feld Umsatzsteuer-ID. Bitte tragen Sie sie hier ein und leeren Sie das Feld Umsatzsteuer-ID.',
      function (value) {
        if (value) return true
        const { vatId } = this.parent
        if (!vatId) return true
        const normalised = normaliseVatId(String(vatId))
        const malformedVatId = !VAT_ID_PATTERN.test(normalised)
        // A Steuernummer is digits and separators — it contains no letters at all. Testing
        // "does not start with two letters" instead was too loose: a mistyped VAT ID like
        // "D123456789" satisfied it, and the user was told to move a broken VAT ID into the
        // Steuernummer field, which is both wrong and leaves them worse off than before.
        const looksLikeTaxNumber = /\d/.test(normalised) && !/[A-Z]/.test(normalised)
        return !(malformedVatId && looksLikeTaxNumber)
      },
    ),

  vatId: Yup.string()
    .nullable()
    .transform(v => (v === '' ? null : v))
    .max(50, 'Maximal 50 Zeichen')
    .test(
      'tax-or-vat-required',
      'Bei Umsatzsteuer > 0 % wird eine Umsatzsteuer-ID oder Steuernummer benötigt',
      function (value) {
        const { taxNumber, vatPercentage } = this.parent
        if (Number(vatPercentage) > 0 && !value && !taxNumber) return false
        return true
      },
    )
    // BT-31 must carry an ISO 3166-1 alpha-2 prefix (BR-CO-09). Without this the value
    // reaches the XML as the seller VAT ID and every recipient's validator rejects the
    // invoice — which is how this surfaced: only when a user uploaded to her tax advisor.
    //
    // Compared case-insensitively so "de123456789" is not refused as malformed. The backend
    // uppercases it on save, so what reaches the XML always carries the uppercase country
    // code the rule requires — accepting lowercase without normalising would reintroduce
    // exactly the defect this check exists to prevent.
    .test(
      'vat-id-format',
      'Die Umsatzsteuer-ID muss mit dem Länderkürzel beginnen, z. B. DE123456789.',
      value => !value || VAT_ID_PATTERN.test(normaliseVatId(value)),
    ),

  vatPercentage: Yup.number()
    .typeError('Bitte eine Zahl eingeben')
    .min(0, 'Wert muss ≥ 0 sein')
    .max(100, 'Wert darf max. 100 sein')
    .required('Dieses Feld muss ausgefüllt sein'),

  invoiceNumberPrefix: Yup.string()
    .matches(/^[A-Z0-9_-]+$/, 'Nur Großbuchstaben, Zahlen, _ oder - erlaubt')
    .default('RE')
    .required('Dieses Feld muss ausgefüllt sein'),

  offerNumberPrefix: Yup.string()
    .nullable()
    .transform(v => (v === '' ? null : v))
    .matches(/^[A-Z0-9_-]+$/, {
      message: 'Nur Großbuchstaben, Zahlen, _ oder - erlaubt',
      excludeEmptyString: true,
    }),

  issuerType: IssuerTypeSchema,

  company: Yup.string()
    .transform(v => (typeof v === 'string' ? v.trim() : v))
    .when('issuerType', {
      is: (t: any) => t === 'COMPANY' || t === 'COMPANY_PERSON',
      then: s => s.min(2, 'Mindestens 2 Zeichen').required('Unternehmen ist erforderlich'),
      otherwise: s =>
        s
          .notRequired()
          .nullable()
          .transform(v => (v === '' ? null : v)),
    }),

  firstName: Yup.string()
    .transform(v => (typeof v === 'string' ? v.trim() : v))
    .when('issuerType', {
      is: (t: any) => t === 'PERSON' || t === 'COMPANY_PERSON',
      then: s => s.min(2, 'Mindestens 2 Zeichen').required('Vorname ist erforderlich'),
      otherwise: s =>
        s
          .notRequired()
          .nullable()
          .transform(v => (v === '' ? null : v)),
    }),

  lastName: Yup.string()
    .transform(v => (typeof v === 'string' ? v.trim() : v))
    .when('issuerType', {
      is: (t: any) => t === 'PERSON' || t === 'COMPANY_PERSON',
      then: s => s.min(2, 'Mindestens 2 Zeichen').required('Nachname ist erforderlich'),
      otherwise: s =>
        s
          .notRequired()
          .nullable()
          .transform(v => (v === '' ? null : v)),
    }),

  street: requiredTrimmed('Straße ist erforderlich').min(2, 'Mindestens 2 Zeichen'),
  streetNumber: requiredTrimmed('Hausnummer ist erforderlich').max(20, 'Maximal 20 Zeichen'),

  postal: Yup.string()
    .transform(v => (typeof v === 'string' ? v.trim() : v))
    .required('Postleitzahl ist erforderlich')
    .max(12, 'Maximal 12 Zeichen'),
  // If you want DE-only:
  // .matches(/^\d{5}$/, 'Ungültige PLZ'),

  city: requiredTrimmed('Stadt ist erforderlich').min(2, 'Mindestens 2 Zeichen'),

  phone: Yup.string()
    .nullable()
    .transform(v => (v === '' ? null : v))
    .max(30, 'Maximal 30 Zeichen'),

  // email is read-only in UI → don't require it here (and don’t show errors)
  email: Yup.string()
    .nullable()
    .transform(v => (v === '' ? null : v))
    .email('Ungültige E-Mail-Adresse'),

  accountHolder: Yup.string().trim().required('Kontoinhaber ist erforderlich'),
})

const ALLOWED_TOKENS = ['{PREFIX}', '{YYYY}', '{YY}', '{MM}', '{RUNNING}'] as const

function extractTokens(tpl: string): string[] {
  return tpl.match(/\{[A-Z]+\}/g) ?? []
}
function hasUnknownTokens(tpl: string) {
  return extractTokens(tpl).some(t => !ALLOWED_TOKENS.includes(t as any))
}

export const InvoiceSettingsAutoInvoiceSchema = Yup.object({
  autoGenerationEnabled: Yup.boolean(),

  autoGenerationDay: Yup.number()
    .nullable()
    .when('autoGenerationEnabled', {
      is: true,
      then: s => s.required('Dieses Feld muss ausgefüllt sein').min(1).max(31),
      otherwise: s => s.nullable(),
    }),

  autoGenerationTime: Yup.string()
    .nullable()
    .when('autoGenerationEnabled', {
      is: true,
      then: s => s.required('Dieses Feld muss ausgefüllt sein'),
      otherwise: s => s.nullable(),
    }),

  activityInvoiceResetPolicy: Yup.mixed<'YEARLY' | 'CONTINUOUS'>()
    .nullable()
    .when('autoGenerationEnabled', {
      is: true,
      then: s => s.required('Reset-Policy ist erforderlich'),
      otherwise: s => s.nullable(),
    }),

  activityInvoiceRunningStart: Yup.string()
    .nullable()
    .when('autoGenerationEnabled', {
      is: true,
      then: s =>
        s
          .matches(/^\d+$/, { message: 'Nur Zahlen erlaubt', excludeEmptyString: true })
          .test('min-1', 'Minimum ist 1', v => {
            if (!v) return true // optional -> defaults to 1
            // numeric compare while allowing "0001"
            return parseInt(v, 10) >= 1
          }),
      otherwise: s => s.nullable(),
    }),

  activityInvoicePrefix: Yup.string()
    .nullable()
    .max(50)
    .when('autoGenerationEnabled', {
      is: true,
      then: s => s.nullable(),
      otherwise: s => s.nullable(),
    }),

  activityInvoiceNumberTemplate: Yup.string()
    .nullable()
    .max(200)
    .when(['autoGenerationEnabled', 'activityInvoiceResetPolicy'], {
      is: (enabled: boolean) => enabled === true,
      then: s =>
        s
          .test('tokens-known', 'Template enthält unbekannte Tokens', v => {
            if (!v) return true // empty -> standard format
            return !hasUnknownTokens(v)
          })
          .test('running-required', '{RUNNING} ist erforderlich', v => {
            if (!v) return true // empty -> standard format
            return v.includes('{RUNNING}')
          })
          .test(
            'yearly-requires-year-token',
            'Bei YEARLY muss {YYYY} oder {YY} enthalten sein',
            function (v) {
              const enabled = this.parent.autoGenerationEnabled
              const policy = this.parent.activityInvoiceResetPolicy
              if (!enabled) return true
              if (!v) return true // empty -> standard format (allowed)
              if (policy !== 'YEARLY') return true

              const trimmed = v.trim()
              if (trimmed === '{RUNNING}') return false
              // also covers templates that effectively contain only RUNNING
              const tokens = extractTokens(trimmed)
              const onlyRunning =
                tokens.length === 1 && tokens[0] === '{RUNNING}' && trimmed === '{RUNNING}'
              if (onlyRunning) return false

              const hasYear = v.includes('{YYYY}') || v.includes('{YY}')
              if (!hasYear) {
                // stricter: if tokens include only RUNNING (and separators), block
                const tokensNoDup = Array.from(new Set(tokens))
                const onlyRunningToken = tokensNoDup.length === 1 && tokensNoDup[0] === '{RUNNING}'
                if (onlyRunningToken) return false
              }
              return true
            }
          ),
      otherwise: s => s.nullable(),
    }),
  sendAfterAutoGeneration: Yup.boolean().nullable(),
  invoicePreTextTemplate: Yup.string().nullable(),
  invoicePostTextTemplate: Yup.string().nullable(),
})

// NORMALIZER
export function normalizeInvoiceSettings(api: Partial<InvoiceSettingsData>): InvoiceSettingsData {
  return {
    ...api,
    bankName: (api.bankName ?? '').trim(),
    iban: (api.iban ?? '').trim(),
    bic: (api.bic ?? '').trim(),

    taxNumber: api.taxNumber ?? '',
    vatId: api.vatId ?? '',
    vatPercentage: api.vatPercentage ?? 19,

    invoiceNumberPrefix: (api.invoiceNumberPrefix ?? '').trim(),
    offerNumberPrefix: (api.offerNumberPrefix ?? '').trim(),

    autoGenerationEnabled: api.autoGenerationEnabled ?? false,
    autoGenerationDay: api.autoGenerationDay ?? null,
    autoGenerationTime: api.autoGenerationTime ?? null,

    invoiceTemplate: api.invoiceTemplate ?? '',
  }
}

export type InvoiceSettingsKeys = keyof InvoiceSettingsData
